class SizeGuideDrawer extends HTMLElement {
  constructor() {
    super();
    this.dataUrl = this.dataset.dataUrl;
    this.brandId = this.dataset.brandId || null;
    this.data = null;
    this.activeCategory = null;
    this.activeGarment = null;
    this.previousFocus = null;
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    this.querySelector('.size-guide-drawer__close').addEventListener('click', () => this.close());
    this.addEventListener('click', (event) => {
      if (!this.classList.contains('is-open')) return;
      if (event.target === this || event.target.classList.contains('size-guide-drawer__overlay')) {
        this.close();
      }
    });
    document.addEventListener('size-guide:open', (event) => {
      if (event.detail && event.detail.brandId) {
        this.brandId = event.detail.brandId;
      }
      this.open();
    });
  }

  async loadData() {
    if (this.data || !this.dataUrl) return;
    try {
      const response = await fetch(this.dataUrl);
      if (!response.ok) throw new Error('HTTP ' + response.status);
      this.data = await response.json();
    } catch (e) {
      console.error('[size-guide] Kunde inte ladda data:', e);
    }
  }

  async open() {
    this.previousFocus = document.activeElement;
    const body = this.querySelector('.size-guide-drawer__body');
    if (body) body.innerHTML = '<div class="size-guide-drawer__loading">Laddar storleksdata...</div>';

    await this.loadData();
    this.render();

    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('size-guide-drawer-open');
    document.addEventListener('keydown', this.handleKeydown);

    requestAnimationFrame(() => {
      const inner = this.querySelector('.size-guide-drawer__inner');
      if (inner) inner.focus();
    });
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('size-guide-drawer-open');
    document.removeEventListener('keydown', this.handleKeydown);
    if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
      this.previousFocus.focus();
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key === 'Tab') this.trapFocus(event);
  }

  trapFocus(event) {
    const focusables = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  render() {
    const brandLabel = this.querySelector('.size-guide-drawer__brand');
    const tabsContainer = this.querySelector('.size-guide-drawer__tabs');
    const body = this.querySelector('.size-guide-drawer__body');

    if (!this.data || !this.data.brands) {
      body.innerHTML = '<p class="size-guide-drawer__error">Kunde inte ladda storleksdata.</p>';
      return;
    }

    const brand = this.data.brands[this.brandId];
    if (!brand) {
      body.innerHTML = `<p class="size-guide-drawer__error">Storleksdata saknas för "${this.escapeHtml(this.brandId || '')}".</p>`;
      return;
    }

    if (brandLabel) brandLabel.textContent = brand.name;

    const categoryKeys = Object.keys(brand.categories || {});
    if (categoryKeys.length === 0) {
      body.innerHTML = '<p class="size-guide-drawer__empty">Inga kategorier tillgängliga.</p>';
      return;
    }

    if (!this.activeCategory || !brand.categories[this.activeCategory]) {
      this.activeCategory = categoryKeys[0];
      this.activeGarment = null;
    }

    this.renderTabs(brand, tabsContainer);
    this.renderBody(brand, body);
  }

  renderTabs(brand, container) {
    container.innerHTML = '';
    Object.entries(brand.categories).forEach(([key, category]) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'size-guide-drawer__tab';
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', key === this.activeCategory ? 'true' : 'false');
      button.textContent = category.label;
      button.addEventListener('click', () => {
        if (this.activeCategory === key) return;
        this.activeCategory = key;
        this.activeGarment = null;
        this.render();
      });
      container.appendChild(button);
    });
  }

  renderBody(brand, body) {
    const category = brand.categories[this.activeCategory];
    body.innerHTML = '';

    const garmentKeys = Object.keys(category.garments || {});
    if (garmentKeys.length === 0) {
      body.innerHTML = '<p class="size-guide-drawer__empty">Inga storleksdata för denna kategori.</p>';
      return;
    }

    if (!this.activeGarment || !category.garments[this.activeGarment]) {
      this.activeGarment = garmentKeys[0];
    }

    const sidebar = document.createElement('div');
    sidebar.className = 'size-guide-drawer__sidebar';
    sidebar.setAttribute('role', 'tablist');
    sidebar.setAttribute('aria-orientation', 'vertical');

    garmentKeys.forEach((key) => {
      const garment = category.garments[key];
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'size-guide-drawer__garment-button';
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', key === this.activeGarment ? 'true' : 'false');
      button.textContent = garment.label;
      button.addEventListener('click', () => {
        if (this.activeGarment === key) return;
        this.activeGarment = key;
        this.renderBody(brand, body);
      });
      sidebar.appendChild(button);
    });

    const content = document.createElement('div');
    content.className = 'size-guide-drawer__content';
    this.renderGarment(category.garments[this.activeGarment], content);

    body.appendChild(sidebar);
    body.appendChild(content);
  }

  renderGarment(garment, container) {
    if (!garment) {
      container.innerHTML = '';
      return;
    }

    const parts = [];

    parts.push(`<h3 class="size-guide-drawer__garment-title">${this.escapeHtml(garment.label)}</h3>`);
    if (garment.description) {
      parts.push(`<p class="size-guide-drawer__garment-description">${this.escapeHtml(garment.description)}</p>`);
    }

    if (garment.size_columns && garment.rows) {
      let table = '<div class="size-guide-drawer__table-wrapper"><table class="size-guide-drawer__table"><thead><tr><th></th>';
      garment.size_columns.forEach((col) => {
        table += `<th>${this.escapeHtml(col)}</th>`;
      });
      table += '</tr>';
      if (garment.subheader && garment.subheader.values) {
        table += `<tr class="size-guide-drawer__table-subheader"><th>${this.escapeHtml(garment.subheader.label || '')}</th>`;
        garment.subheader.values.forEach((value) => {
          table += `<th>${this.escapeHtml(value)}</th>`;
        });
        table += '</tr>';
      }
      table += '</thead><tbody>';
      garment.rows.forEach((row) => {
        table += `<tr><td>${this.escapeHtml(row.label)}</td>`;
        (row.values || []).forEach((value) => {
          table += `<td>${this.escapeHtml(value)}</td>`;
        });
        table += '</tr>';
      });
      table += '</tbody></table></div>';
      parts.push(table);
    }

    if (garment.instructions && garment.instructions.length > 0) {
      let instr = '<div class="size-guide-drawer__instructions">';
      instr += '<h4 class="size-guide-drawer__instructions-title">Så mäter du</h4>';
      instr += '<ul class="size-guide-drawer__instructions-list">';
      garment.instructions.forEach((item) => {
        instr += '<li>';
        instr += `<span class="size-guide-drawer__instructions-point" aria-hidden="true">${this.escapeHtml(item.point || '')}</span>`;
        instr += `<span><strong>${this.escapeHtml(item.label || '')}:</strong> ${this.escapeHtml(item.text || '')}</span>`;
        instr += '</li>';
      });
      instr += '</ul></div>';
      parts.push(instr);
    }

    container.innerHTML = parts.join('');
  }

  escapeHtml(value) {
    if (value === null || value === undefined) return '';
    return String(value).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[c]));
  }
}

customElements.define('size-guide-drawer', SizeGuideDrawer);

/*
  SizeGuidePage - inline version för dedikerade storleksguide-sidor.
  Återanvänder SizeGuideDrawer:s render-logik (renderTabs/renderBody/renderGarment)
  via arv, men har ingen drawer-chrome (overlay, close-knapp, focus trap).
  Lägger till brand-väljare högst upp eftersom användaren kommer hit utan
  förvald brand (till skillnad från produktsidan).
*/
class SizeGuidePage extends SizeGuideDrawer {
  async connectedCallback() {
    await this.loadData();

    if (this.data && this.data.brands) {
      const brandKeys = Object.keys(this.data.brands);
      if (!this.brandId && brandKeys.length > 0) {
        this.brandId = brandKeys[0];
      }
    }

    this.renderBrandTabs();
    this.render();
  }

  renderBrandTabs() {
    const container = this.querySelector('.size-guide-page__brand-tabs');
    if (!container || !this.data || !this.data.brands) return;

    container.innerHTML = '';
    Object.entries(this.data.brands).forEach(([key, brand]) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'size-guide-page__brand-tab';
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', key === this.brandId ? 'true' : 'false');
      button.textContent = brand.name;
      button.addEventListener('click', () => {
        if (this.brandId === key) return;
        this.brandId = key;
        this.activeCategory = null;
        this.activeGarment = null;
        this.renderBrandTabs();
        this.render();
      });
      container.appendChild(button);
    });
  }
}

customElements.define('size-guide-page', SizeGuidePage);

class SizeGuideButton extends HTMLElement {
  connectedCallback() {
    const button = this.querySelector('button');
    if (!button) return;
    button.addEventListener('click', () => {
      document.dispatchEvent(
        new CustomEvent('size-guide:open', {
          detail: { brandId: this.dataset.brandId },
        })
      );
    });
  }
}

customElements.define('size-guide-button', SizeGuideButton);
